import React, { FunctionComponent, useState, useEffect, useCallback } from 'react';
import { OptionalFileUploadQuestion } from './OptionalFileUploadQuestion';
import { TextWithSelectQuestion } from './TextWithSelectQuestion';
import { NumberWithSelectQuestion } from './NumberWithSelectQuestion';
import * as QS from './question-shared';
import * as QH from './QuestionHelpers';
import { NumericFormat } from 'react-number-format';
import Select, { StylesConfig } from 'react-select';
import DateBox from 'devextreme-react/date-box';
import 'devextreme/dist/css/dx.light.css';

export const Question: FunctionComponent<QS.QuestionProps> = ({
    children,
    selector,
    type,
    value,
    onChange,
    placeholder,
    values,
    styleName,
    submitted,
    onQuestionValidate, // Used to set validation property value on the main page (BorrowerPage.tsx, etc)
    onChangeQualifier,
    fileType,
    onFileSelected,
    disabled,
    options,
    option,
    styles,
    maxLength = undefined,
    required,
}) => {
    const [showValidationMessage, setShowValidationMessage] = useState<boolean>(false);
    const [passing, setPassing] = useState<boolean>(
        onQuestionValidate === undefined || QS.validValue(type, value, option, options)
    );

    const validationLabel = React.useMemo(() => QS.reactNodeToString(children), [children]);

    useEffect(() => {
        setShowValidationMessage(!passing && submitted);
        onQuestionValidate && onQuestionValidate(selector, passing, validationLabel);

        // if this component unmounts, will advise parent that it's valid now, i.e., no longer present
        // and so any prior validation messages should be ignored
        return () => {
            onQuestionValidate && onQuestionValidate(selector, true, validationLabel);
        };
    }, [passing, submitted, validationLabel, selector, onQuestionValidate]);

    const computedStyle = {
        ...QS.styles.default,
        ...(styleName ? QS.styles[styleName] : {}),
    };

    useEffect(() => {
        setPassing(onQuestionValidate === undefined || QS.validValue(type, value, option, options));
    }, [value, type, option, options, onChange, onQuestionValidate]);

    const handleChange: QS.onChangeType = useCallback(
        (newValue: QS.QuestionValueType) => {
            // istanbul ignore else
            if (onChange) {
                if (onChangeQualifier !== undefined) {
                    onChange && (onChange as QS.onChangeWithQualifierType)(onChangeQualifier, newValue);
                } else {
                    onChange && (onChange as QS.onChangeType)(newValue);
                }
            }
        },
        [onChange, onChangeQualifier]
    );

    switch (type) {
        case QS.QuestionType.YesNo: {
            return (
                <fieldset
                    id={`yes-no-${selector}`}
                    data-testid={`yes-no-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].fieldset}
                >
                    <legend
                        style={computedStyle.legend}
                        className={styleName && QS.classNames[styleName].legend}
                    >
                        {QH.childrenFormatted(children, required)}
                    </legend>
                    <div className={`yes-no ${styleName && QS.classNames[styleName].input}`}>
                        <label htmlFor={`${selector}-yes`}>
                            <input
                                type='radio'
                                name={selector}
                                id={`${selector}-yes`}
                                data-testid={`${selector}-yes`}
                                checked={value === true}
                                onChange={() => handleChange(true)}
                                disabled={disabled}
                            />
                            Yes
                        </label>
                        <label htmlFor={`${selector}-no`}>
                            <input
                                type='radio'
                                name={selector}
                                id={`${selector}-no`}
                                data-testid={`${selector}-no`}
                                checked={value === false}
                                onChange={() => handleChange(false)}
                                disabled={disabled}
                            />
                            No
                        </label>
                        {showValidationMessage && QS.validationMessage(type, validationLabel)}
                    </div>
                </fieldset>
            );
        }
        case QS.QuestionType.YesNoUnsure: {
            return (
                <fieldset
                    id={`yes-no-${selector}`}
                    data-testid={`yes-no-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].fieldset}
                >
                    <legend
                        style={computedStyle.legend}
                        className={styleName && QS.classNames[styleName].legend}
                    >
                        {QH.childrenFormatted(children, required)}
                    </legend>
                    <div className={`yes-no ${styleName && QS.classNames[styleName].input}`}>
                        <label htmlFor={`${selector}-yes`}>
                            <input
                                type='radio'
                                name={selector}
                                id={`${selector}-yes`}
                                data-testid={`${selector}-yes`}
                                checked={value === 'yes'}
                                onChange={() => handleChange('yes')}
                                disabled={disabled}
                            />
                            Yes
                        </label>
                        <label htmlFor={`${selector}-no`}>
                            <input
                                type='radio'
                                name={selector}
                                id={`${selector}-no`}
                                data-testid={`${selector}-no`}
                                checked={value === 'no'}
                                onChange={() => handleChange('no')}
                                disabled={disabled}
                            />
                            No
                        </label>
                        <label htmlFor={`${selector}-unsure`}>
                            <input
                                type='radio'
                                name={selector}
                                id={`${selector}-unsure`}
                                data-testid={`${selector}-unsure`}
                                checked={value === 'unsure'}
                                onChange={() => handleChange('unsure')}
                                disabled={disabled}
                            />
                            Unsure
                        </label>
                        {showValidationMessage && QS.validationMessage(type, validationLabel)}
                    </div>
                </fieldset>
            );
        }

        case QS.QuestionType.RequiredCheckbox: {
            return (
                <fieldset
                    id={`yes-no-${selector}`}
                    data-testid={`yes-no-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].fieldset}
                >
                    <legend
                        style={computedStyle.legend}
                        className={styleName && QS.classNames[styleName].legend}
                    >
                        {QH.childrenFormatted(children, required)}
                    </legend>
                    <div className={`yes-no ${styleName && QS.classNames[styleName].input}`}>
                        <input
                            type='checkbox'
                            className='big-checkbox'
                            name={selector}
                            id={`${selector}-checkbox`}
                            data-testid={`${selector}-checkbox`}
                            checked={!!value}
                            onChange={(e) => handleChange(e.target.checked)}
                            disabled={disabled}
                            style={{ margin: '4px 0 0 0' }}
                        />
                        {showValidationMessage && QS.validationMessage(type, validationLabel)}
                    </div>
                </fieldset>
            );
        }
        case QS.QuestionType.Date: {
            return (
                <fieldset
                    id={`date-${selector}`}
                    data-testid={`date-${selector}`}
                    style={computedStyle.fieldset}
                    className='columns small-12'
                >
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            alignContent: 'flex-start',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ flex: 1, marginTop: '5px' }}>
                            <legend
                                id={`date-${selector}`}
                                style={{ ...computedStyle.legend }}
                            >
                                {QH.childrenFormatted(children, required)}
                            </legend>
                        </div>

                        <div>
                            <DateBox
                                id={selector}
                                value={value ? new Date((value as string) + ' 00:00') : undefined}
                                data-testid={`date-${selector}`}
                                // style={{marginBottom: '0'}}
                                disabled={disabled}
                                onValueChange={handleChange}
                                type='date'
                            />
                        </div>
                        {/* div tag below shifts the select to the left instead of right justified */}
                        {/* <div style={{ alignContent: 'flex-end', flex: 2, marginRight: '10px' }}></div> */}
                    </div>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }

        case QS.QuestionType.MultilineText: {
            return (
                <fieldset
                    id={`date-${selector}`}
                    data-testid={`date-${selector}`}
                    style={computedStyle.fieldset}
                    className='columns small-12'
                >
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            alignContent: 'flex-start',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ flex: 1, marginTop: '5px' }}>
                            <legend
                                id={`multiline-${selector}`}
                                style={{ ...computedStyle.legend }}
                            >
                                {QH.childrenFormatted(children, required)}
                            </legend>
                        </div>

                        <div style={{ marginRight: '10px' }}>
                            <textarea
                                id={selector}
                                data-testid={`multiline-${selector}`}
                                value={value as string}
                                style={{ resize: 'none' }}
                                disabled={disabled}
                                cols={40}
                                rows={5}
                                onChange={(e) => handleChange(e.target.value)}
                            ></textarea>
                        </div>
                        {/* div tag below shifts the select to the left instead of right justified */}
                        {/* <div style={{ alignContent: 'flex-end', flex: 2, marginRight: '10px' }}></div> */}
                    </div>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }

        case QS.QuestionType.RowSelect: {
            return (
                <fieldset
                    id={`select-${selector}`}
                    data-testid={`select-${selector}`}
                    style={computedStyle.fieldset}
                    className='columns small-12'
                >
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            alignContent: 'flex-start',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <legend
                                id={`select-${selector}`}
                                style={{ ...computedStyle.legend }}
                            >
                                {QH.childrenFormatted(children, required)}
                            </legend>
                        </div>

                        <div>
                            <select
                                id={selector}
                                style={{ marginRight: '32px' }}
                                value={value as string}
                                onChange={(e) => {
                                    handleChange(e.target.value);
                                }}
                                disabled={disabled}
                            >
                                {values?.map((v, i) => (
                                    <option
                                        key={i}
                                        value={v.value}
                                    >
                                        {v.display}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {/* div tag below shifts the select to the left instead of right justified */}
                        {/* <div style={{ alignContent: 'flex-end', flex: 2, marginRight: '10px' }}></div> */}
                    </div>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }
        case QS.QuestionType.ColumnSelect: {
            return (
                <fieldset
                    id={`select-${selector}`}
                    data-testid={`select-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].select}
                >
                    <label
                        htmlFor={selector}
                        style={{ lineHeight: '1.5' }}
                    >
                        {QH.childrenFormatted(children, required)}
                    </label>
                    <select
                        id={selector}
                        data-testid={`question-select-${selector}`}
                        value={value as string}
                        onChange={(e) => handleChange(e.target.value)}
                        disabled={disabled}
                    >
                        {values?.map((v, i) => (
                            <option
                                key={i}
                                value={v.value}
                            >
                                {v.display}
                            </option>
                        ))}
                    </select>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }
        case QS.QuestionType.RowSelector: {
            return (
                <div
                    id={`row-selector-${selector}`}
                    data-testid={`row-selector-${selector}`}
                    className='row inline-label'
                    style={computedStyle.fieldset}
                >
                    <div
                        className='columns small-12 medium-6 large-7'
                        style={computedStyle.legendContainer}
                    >
                        <label
                            htmlFor={selector}
                            style={computedStyle.legend}
                        >
                            {QH.childrenFormatted(children, required)}
                        </label>
                    </div>
                    <div
                        className='columns small-12 medium-6 large-5'
                        style={computedStyle.inputContainer}
                    >
                        <select
                            id={selector}
                            data-testid={selector}
                            value={value as string}
                            onChange={(e) => handleChange(e.target.value)}
                            disabled={disabled}
                        >
                            {values?.map((v, i) => (
                                <option
                                    key={i}
                                    value={v.value}
                                    disabled={v.value === 'disabled'}
                                >
                                    {v.display}
                                </option>
                            ))}
                        </select>
                    </div>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </div>
            );
        }
        case QS.QuestionType.EntitySelect:
        case QS.QuestionType.LOCBorrowerSelect:
        case QS.QuestionType.StyledRowSelector: {
            return (
                <div
                    id={`row-selector-${selector}`}
                    data-testid={`row-selector-${selector}`}
                    className='row inline-label'
                    style={computedStyle.fieldset}
                >
                    <div
                        className='columns small-12 medium-6 large-7'
                        style={computedStyle.legendContainer}
                    >
                        <label
                            htmlFor={selector}
                            style={computedStyle.legend}
                        >
                            {QH.childrenFormatted(children, required)}
                        </label>
                    </div>
                    <div
                        className='columns small-12 medium-6 large-5'
                        style={computedStyle.inputContainer}
                    >
                        <Select
                            className={`${selector}-select`}
                            options={options}
                            value={option}
                            onChange={(e) => handleChange(e.value)}
                            styles={styles}
                            isSearchable
                            isOptionDisabled={(option) => option.isDisabled}
                        />
                    </div>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </div>
            );
        }
        case QS.QuestionType.RequiredConfirmEntity: {
            return (
                <fieldset
                    id={`row-selector-${selector}`}
                    data-testid={`row-selector-${selector}`}
                    className='row inline-label'
                    style={{ ...computedStyle.fieldset, margin: '1rem 0 0 0' }}
                >
                    <div
                        className='columns small-12 medium-6 large-4'
                        style={computedStyle.legendContainer}
                    >
                        <label
                            htmlFor={selector}
                            style={computedStyle.legend}
                        >
                            {QH.childrenFormatted(children, required)}
                        </label>
                    </div>
                    <div
                        className='columns small-12 medium-6 large-5'
                        style={computedStyle.inputContainer}
                    >
                        <select
                            id={selector}
                            data-testid={selector}
                            value={value as string}
                            onChange={(e) => handleChange(e.target.value)}
                            disabled={disabled}
                        >
                            {values?.map((v, i) => (
                                <option
                                    key={i}
                                    value={v.value}
                                    disabled={v.value === 'disabled'}
                                >
                                    {v.display}
                                </option>
                            ))}
                        </select>
                    </div>
                    {showValidationMessage && QS.validationMessage(type, validationLabel, value as string)}
                </fieldset>
            );
        }
        case QS.QuestionType.EntityStateIncorporated: {
            return (
                <fieldset
                    id={`row-selector-${selector}`}
                    data-testid={`row-selector-${selector}`}
                    className='row inline-label'
                    style={{ ...computedStyle.fieldset, margin: '1rem 0 0 0' }}
                >
                    <div
                        className='columns small-12 medium-6 large-4'
                        style={computedStyle.legendContainer}
                    >
                        <label
                            htmlFor={selector}
                            style={{ fontWeight: 'normal' }}
                        >
                            {QH.childrenFormatted(children, required)}
                        </label>
                    </div>
                    <div
                        className='columns small-12 medium-6 large-4'
                        style={computedStyle.inputContainer}
                    >
                        <select
                            id={selector}
                            data-testid={selector}
                            value={value as string}
                            onChange={(e) => handleChange(e.target.value)}
                            disabled={disabled}
                            required={true}
                        >
                            {values?.map((v, i) => (
                                <option
                                    key={i}
                                    value={v.value}
                                    disabled={v.value === 'disabled'}
                                >
                                    {v.display}
                                </option>
                            ))}
                        </select>
                    </div>
                    {showValidationMessage && QS.validationMessage(type, validationLabel, value as string)}
                </fieldset>
            );
        }

        case QS.QuestionType.TextWithSelect: {
            return (
                <TextWithSelectQuestion
                    selector={selector}
                    styleName={styleName}
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    values={values}
                    showValidationMessage={showValidationMessage}
                    onChange={handleChange}
                    onQuestionValidate={onQuestionValidate}
                    validationLabel={validationLabel}
                    disabled={disabled}
                >
                    {children}
                </TextWithSelectQuestion>
            );
        }
        case QS.QuestionType.TextWithNumber:
        case QS.QuestionType.RestrictedUnits: {
            return (
                <fieldset
                    id={`text-with-select-${selector}`}
                    data-testid={`text-with-select-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].fieldset}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <legend
                            id={`text-with-select-label-${selector}`}
                            style={{
                                ...computedStyle.legend,
                                alignContent: 'flex-start',
                                flex: 3,
                            }}
                            className={styleName && QS.classNames[styleName].legend}
                        >
                            {QH.childrenFormatted(children, required)}
                        </legend>
                        <NumericFormat
                            id={`text-with-select-textbox-${selector}`}
                            data-testid={`text-with-select-textbox-${selector}`}
                            aria-labelledby={`text-with-select-label-${selector}`}
                            value={value as string}
                            placeholder={placeholder}
                            thousandSeparator={true}
                            decimalScale={0}
                            onValueChange={(values) => {
                                const { value } = values;
                                handleChange(value);
                            }}
                            style={{
                                ...computedStyle.input,
                                maxWidth: '100%',
                                textAlign: 'right',
                                paddingRight: '8px',
                            }}
                            className={styleName && QS.classNames[styleName].input}
                            disabled={disabled}
                        />
                    </div>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }
        case QS.QuestionType.NumberWithSelect: {
            return (
                <NumberWithSelectQuestion
                    selector={selector}
                    styleName={styleName}
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    values={values}
                    showValidationMessage={showValidationMessage}
                    onChange={handleChange}
                    onQuestionValidate={onQuestionValidate}
                    validationLabel={validationLabel}
                    disabled={disabled}
                >
                    {QH.childrenFormatted(children, required)}
                </NumberWithSelectQuestion>
            );
        }

        case QS.QuestionType.OptionalFileUpload: {
            return (
                <OptionalFileUploadQuestion
                    selector={selector}
                    styleName={styleName}
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    showValidationMessage={showValidationMessage}
                    onChange={onChange}
                    onChangeQualifier={onChangeQualifier}
                    onQuestionValidate={onQuestionValidate}
                    validationLabel={validationLabel}
                    fileType={fileType}
                    onFileSelected={onFileSelected}
                    disabled={disabled}
                >
                    {QH.childrenFormatted(children, required)}
                </OptionalFileUploadQuestion>
            );
        }
        case QS.QuestionType.NonZeroCurrency:
        case QS.QuestionType.Currency: {
            return (
                <fieldset
                    id={`text-${selector}`}
                    data-testid={`text-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].fieldset}
                >
                    <legend
                        id={`legend-${selector}`}
                        style={computedStyle.legend}
                        className={styleName && QS.classNames[styleName].legend}
                    >
                        {QH.childrenFormatted(children, required)}
                    </legend>
                    <span className={'has-dollar-form'}>
                        <NumericFormat
                            id={selector}
                            aria-labelledby={`legend-${selector}`}
                            value={value as string}
                            placeholder={placeholder || '$ 0'}
                            thousandSeparator={true}
                            prefix={'$ '}
                            decimalScale={2}
                            onValueChange={(values) => {
                                const { value } = values;
                                handleChange(value);
                            }}
                            style={computedStyle.input}
                            className={styleName && QS.classNames[styleName].input}
                            disabled={disabled}
                            // type={QS.inputType(type)}
                        />
                    </span>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }
        case QS.QuestionType.TargetLoanRequest:
        case QS.QuestionType.CurrencyNoDecimals: {
            return (
                <fieldset
                    id={`text-${selector}`}
                    data-testid={`text-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].fieldset}
                >
                    <legend
                        id={`legend-${selector}`}
                        style={computedStyle.legend}
                        className={styleName && QS.classNames[styleName].legend}
                    >
                        {QH.childrenFormatted(children, required)}
                    </legend>
                    <span className={'has-dollar-form'}>
                        <NumericFormat
                            id={selector}
                            aria-labelledby={`legend-${selector}`}
                            value={value as string}
                            placeholder={placeholder || '$ 0'}
                            thousandSeparator={true}
                            prefix={'$ '}
                            decimalScale={0}
                            onValueChange={(values) => {
                                const { value } = values;
                                handleChange(value);
                            }}
                            style={computedStyle.input}
                            className={styleName && QS.classNames[styleName].input}
                            disabled={disabled}
                            // type={QS.inputType(type)}
                        />
                    </span>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }

        case QS.QuestionType.Zipcode: {
            return (
                <fieldset
                    id={`text-${selector}`}
                    data-testid={`text-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].fieldset}
                >
                    <legend
                        id={`legend-${selector}`}
                        style={computedStyle.legend}
                        className={styleName && QS.classNames[styleName].legend}
                    >
                        {QH.childrenFormatted(children, required)}
                    </legend>
                    <span>
                        {`${value}`.length < 5 ? (
                            <NumericFormat
                                format='#####'
                                mask='_'
                                allowLeadingZeros={true}
                                id={selector}
                                aria-labelledby={`legend-${selector}`}
                                placeholder='12345'
                                name='zip'
                                value={value as string}
                                onValueChange={(values) => {
                                    const { value } = values;
                                    handleChange(value);
                                }}
                                className={styleName && QS.classNames[styleName].input}
                                style={computedStyle.input}
                                disabled={disabled}
                            />
                        ) : `${value}`.length === 5 ? (
                            <NumericFormat
                                allowLeadingZeros={true}
                                id={selector}
                                aria-labelledby={`legend-${selector}`}
                                placeholder='12345'
                                name='zip'
                                value={value as string}
                                onValueChange={(values) => {
                                    const { value } = values;
                                    handleChange(value);
                                }}
                                className={styleName && QS.classNames[styleName].input}
                                style={computedStyle.input}
                                disabled={disabled}
                            />
                        ) : (
                            <NumericFormat
                                format='#####-####'
                                mask='_'
                                allowLeadingZeros={true}
                                id={selector}
                                aria-labelledby={`legend-${selector}`}
                                placeholder='12345'
                                name='zip'
                                value={value as string}
                                onValueChange={(values) => {
                                    const { value } = values;
                                    handleChange(value);
                                }}
                                className={styleName && QS.classNames[styleName].input}
                                style={computedStyle.input}
                                disabled={disabled}
                            />
                        )}
                    </span>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }
        case QS.QuestionType.OptionalPhone:
        case QS.QuestionType.Phone: {
            return (
                <fieldset
                    id={`text-${selector}`}
                    data-testid={`text-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].fieldset}
                >
                    <legend
                        id={`legend-${selector}`}
                        style={computedStyle.legend}
                        className={styleName && QS.classNames[styleName].legend}
                    >
                        {QH.childrenFormatted(children, required)}
                    </legend>
                    <span>
                        <NumericFormat
                            format='(###) ###-####'
                            mask='_'
                            id={selector}
                            aria-labelledby={`legend-${selector}`}
                            placeholder='(212) 555-1212'
                            name='phone'
                            value={value as string}
                            onValueChange={(values) => {
                                const { value } = values;
                                handleChange(value);
                            }}
                            disabled={disabled}
                            className={styleName && QS.classNames[styleName].input}
                            style={computedStyle.input}
                        />
                    </span>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }
        case QS.QuestionType.RequiredEntityName: {
            return (
                <fieldset
                    id={`text-${selector}`}
                    data-testid={`text-${selector}`}
                    className='row inline-label'
                    style={{ ...computedStyle.fieldset, margin: '1rem 0 0 0' }}
                >
                    <div
                        className='columns small-12 medium-6 large-4'
                        style={computedStyle.legendContainer}
                    >
                        <label
                            htmlFor={selector}
                            style={{ fontWeight: 'normal' }}
                        >
                            {QH.childrenFormatted(children, required)}
                        </label>
                    </div>
                    <div
                        className='columns small-12 medium-6 large-8'
                        style={computedStyle.inputContainer}
                    >
                        <input
                            type={QS.inputType(type)}
                            id={selector}
                            data-testid={selector}
                            value={value as string}
                            style={computedStyle.input}
                            onChange={(e) => handleChange(e.target.value)}
                            placeholder={placeholder}
                            aria-labelledby={`legend-${selector}`}
                            disabled={disabled}
                            maxLength={100}
                        />
                    </div>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }

        default: {
            //defaults to a text field
            return (
                <fieldset
                    id={`text-${selector}`}
                    data-testid={`text-${selector}`}
                    style={computedStyle.fieldset}
                    className={styleName && QS.classNames[styleName].fieldset}
                >
                    <legend
                        id={`legend-${selector}`}
                        style={computedStyle.legend}
                        className={styleName && QS.classNames[styleName].legend}
                    >
                        {QH.childrenFormatted(children, required)}
                    </legend>
                    <span>
                        <input
                            type={QS.inputType(type)}
                            id={selector}
                            data-testid={selector}
                            value={value as string}
                            className={styleName && QS.classNames[styleName].input}
                            style={computedStyle.input}
                            onChange={(e) => handleChange(e.target.value)}
                            placeholder={placeholder}
                            aria-labelledby={`legend-${selector}`}
                            disabled={disabled}
                            maxLength={maxLength}
                        />
                    </span>
                    {showValidationMessage && QS.validationMessage(type, validationLabel)}
                </fieldset>
            );
        }
    }
};
